import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useState } from 'react';
import { Pressable, StyleSheet, Text, View } from 'react-native';

import type { AssociationSettingsHandle } from './association-settings-handle';

import { useAccountingPermissions } from '@/hooks/use-accounting';
import { useUpdateAssociation } from '@/hooks/use-data';
import type { AccountingBasis, Association } from '@/types/models';
import { Alert } from '@/utils/alert';

interface Props {
	/** Edit mode: the existing association. Omit for the create (draft) flow —
	 *  the section starts at "No preference" and `save(id)` is called by the
	 *  parent after the association is created. */
	association?: Association;
	/** When true, the parent owns saving via the imperative handle. */
	hideSaveButton?: boolean;
}

type Choice = AccountingBasis | 'none';

const CHOICES: { value: Choice; label: string; description: string }[] = [
	{ value: 'accrual', label: 'Accrual', description: 'Income/expense recognized when posted; default for most associations.' },
	{ value: 'cash', label: 'Cash', description: 'Income/expense recognized only when paid. Useful for tax-style reporting.' },
	{ value: 'none', label: 'No preference', description: 'Reports open with the app-wide default (Accrual). Each user picks per visit.' },
];

const toChoice = (a: Association | undefined): Choice => (a?.defaultAccountingBasis ?? 'none');

export const AssociationBasisSettings = forwardRef<AssociationSettingsHandle, Props>(({ association, hideSaveButton }, ref) => {
	const { canModify } = useAccountingPermissions();
	const update = useUpdateAssociation();
	const persistedChoice = toChoice(association);
	const [choice, setChoice] = useState<Choice>(persistedChoice);

	// Re-sync the local selection when the persisted basis changes or the
	// user switches to a different association. Depend on the derived
	// primitive + id rather than the whole `association` object, which would
	// re-run on every render.
	useEffect(() => {
		setChoice(persistedChoice);
	}, [association?.id, persistedChoice]);

	const dirty = choice !== persistedChoice;

	const persist = useCallback(async (associationId: string): Promise<void> => {
		await update.mutateAsync({
			id: associationId,
			// `null` clears the column back to NULL on the server; the API
			// schema accepts `'cash' | 'accrual' | null | undefined`.
			data: { defaultAccountingBasis: choice === 'none' ? null : choice },
		});
	}, [choice, update]);

	/**
	 * `dirty` already existed above (it enables the inline Save button) but the handle
	 * ignored it, so the old condition only skipped the create flow's "No preference"
	 * default — in edit mode `association` is always set, and every association save
	 * rewrote the basis untouched. Reuse the signal that was already correct.
	 */
	useImperativeHandle(ref, () => ({
		save: async (associationId: string) => {
			if (!dirty) return;
			await persist(associationId);
		},
	}), [dirty, persist]);

	const save = async (): Promise<void> => {
		if (!association) return;
		try {
			await persist(association.id);
		} catch (e) {
			Alert.alert('Save failed', e instanceof Error ? e.message : 'Unknown error');
		}
	};

	return (
		<View style={styles.section}>
			<Text style={styles.sectionTitle}>Default basis</Text>
			<Text style={styles.help}>
				Pre-selects the cash/accrual chip on the Balance Sheet, Profit &amp; Loss,
				Trial Balance, and GL Report when this association is opened. Users can
				still flip the chip on the page; this only sets the starting value.
			</Text>
			<View style={styles.chipRow}>
				{CHOICES.map((c) => (
					<Pressable
						key={c.value}
						onPress={() => canModify && setChoice(c.value)}
						style={[styles.chip, choice === c.value && styles.chipActive, !canModify && styles.chipDisabled]}
						accessibilityRole="button"
						accessibilityState={{ selected: choice === c.value, disabled: !canModify }}
					>
						<Text style={[styles.chipLabel, choice === c.value && styles.chipLabelActive]}>{c.label}</Text>
					</Pressable>
				))}
			</View>
			<Text style={styles.caption}>{CHOICES.find((c) => c.value === choice)?.description ?? ''}</Text>

			{canModify && !hideSaveButton && (
				<Pressable
					onPress={save}
					style={[styles.primary, (!dirty || update.isPending) && styles.primaryDisabled]}
					disabled={!dirty || update.isPending}
				>
					<Text style={styles.primaryLabel}>
						{update.isPending ? 'Saving…' : dirty ? 'Save default basis' : 'Saved'}
					</Text>
				</Pressable>
			)}
		</View>
	);
});

AssociationBasisSettings.displayName = 'AssociationBasisSettings';

const styles = StyleSheet.create({
	section: { marginTop: 12, padding: 14, backgroundColor: '#f5f7fa', borderRadius: 8 },
	sectionTitle: {
		fontSize: 14, fontWeight: '700', color: '#212121', marginBottom: 8,
		textTransform: 'uppercase', letterSpacing: 0.4,
	},
	help: { fontSize: 12, color: '#546e7a', marginBottom: 10 },
	chipRow: { flexDirection: 'row', gap: 8, flexWrap: 'wrap' },
	chip: {
		paddingVertical: 8, paddingHorizontal: 14, borderRadius: 14,
		backgroundColor: '#e0e0e0',
	},
	chipActive: { backgroundColor: '#1976d2' },
	chipDisabled: { opacity: 0.6 },
	chipLabel: { fontSize: 12, color: '#333' },
	chipLabelActive: { color: '#fff', fontWeight: '600' },
	caption: { fontSize: 12, color: '#546e7a', fontStyle: 'italic', marginTop: 10 },
	primary: { backgroundColor: '#007AFF', padding: 12, borderRadius: 6, alignItems: 'center', marginTop: 12 },
	primaryDisabled: { opacity: 0.5 },
	primaryLabel: { color: '#fff', fontWeight: '600' },
});
